import { IonRouterLink, useIonViewWillEnter } from '@ionic/react';
import { Comment, FullComment, Post, PostTypes, getSort, reverseSort } from 'model';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { RootState } from '../App';
import { useGetAllCommentsForPostQuery, useGetCommunityQuestionStarsQuery } from '../api/CommentApi';
import { useGetPostQuery } from '../api/PostApi';
import { Header } from '../components/Header';
import { IconButton } from '../components/IconButton';
import { EventLikeDislike, PostLikeDislike } from '../components/LikeDislike';
import { NestedCommentItem, getCommentDepth } from '../components/NestedCommentItem';
import { NoResults } from '../components/NoResults';
import { PostListItem } from '../components/PostListItem';
import { PrivateConversationBanner } from '../components/PrivateConversationBanner.js';
import { IconArrowBack, IconComments, IconEdit, IconEyeOff, IconSadFace } from '../components/QuirkIcons';
import { QuirkIonContent } from '../components/QuirkIonContent';
import { QuirkIonPage } from '../components/QuirkIonPage';
import { QuirkLoader } from '../components/QuirkLoader';
import { useCurrentUser } from '../hooks/useCurrentUser.js';
import { CommentDeleteState } from '../state/commentViewSlice';
import { LoginState } from '../state/loginSlice';
import styles from './PostDetail.module.css';
import { RealUserOnly } from './admin/RealUserOnly';
import { AnonymousOnly } from './admin/AnonymousOnly.js';
import { FeedLogin } from '../components/FeedLogin.js';

interface PostDetailParams {
	postid: Post['_id'];
	expanded: string;
	commentid?: Comment['_id'];
}

function getLoadingMessage(postType?: PostTypes): string {
	switch (postType) {
		case 'marketplaceItem':
			return 'messages';
		case 'communityQuestion':
			return 'answers';
		default:
			return 'comments';
	}
}

export const PostDetail: React.FC = () => {
	const user = useCurrentUser();
	const { postid, expanded, commentid } = useParams<PostDetailParams>();
	const { data: post, isLoading } = useGetPostQuery(postid, { skip: !postid });
	const { data: starredComments } = useGetCommunityQuestionStarsQuery({ questionId: postid });
	const state: LoginState = useSelector<RootState, LoginState>(state => state.login);
	const commentState: CommentDeleteState = useSelector<RootState, CommentDeleteState>(
		state => state.commentDeleteState
	);

	const history = useHistory();
	// Find our base path
	let {
		data: commentData,
		refetch,
		isSuccess,
		isLoading: commentsLoading
	} = useGetAllCommentsForPostQuery(post?._id, { skip: !post?._id });
	commentData = [...(commentData || [])];
	interface CommentWithTotalStars extends FullComment {
		stars?: number;
	}
	const commentDataWithTotalStars: CommentWithTotalStars[] = useMemo(() => {
		if (post?.postType === 'communityQuestion') {
			if (!commentData || commentData.length === 0) return [];
			const sortedData = commentData.map(comment => ({
				...comment,
				stars: starredComments?.[comment._id] !== undefined ? starredComments[comment._id] : undefined
			}));
			sortedData.sort(reverseSort(getSort('stars')));
			return sortedData;
		} else {
			if (!commentData || commentData.length === 0) return [];
			return commentData.sort(reverseSort(getSort('created')));
		}
	}, [starredComments, commentData, post]);

	// Scroll to comment if provided as a parm
	useEffect(() => {
		if (commentid && isSuccess) {
			// Wait for the page to render the comments
			setTimeout(() => {
				const id = document.getElementById(commentid);
				if (id) {
					id.scrollIntoView({ behavior: 'smooth' });
				}
			}, 500);
		}
	}, [isSuccess, commentid]);

	useIonViewWillEnter(() => {
		try {
			refetch();
		} catch (e) {
			// ignore, might not have been fetched yet
		}
	}, [refetch]);

	const goBack = () => {
		if (history.length <= 1) {
			history.push('/main/latest');
		} else {
			history.go(-1);
		}
	};

	return (
		<QuirkIonPage roles={['anonymous', 'user']} name="Post Detail" data={{ post: postid }}>
			<Header
				left={<IconButton icon={<IconArrowBack />} onClick={goBack} />}
				right={
					<>
						{state.user._id === post?.author ? (
							<IconButton
								onClick={() => {
									if (post.postType !== 'businessPost' && post.postType !== 'businessCheckIn') {
										history.push(`/main/latest/${post.postType}/edit/${post?._id}`);
									} else {
										history.push(`/main/latest/bus/${post.businessId}/edit/${post?._id}`);
									}
								}}
								label="Edit"
								icon={<IconEdit />}
							/>
						) : (
							<RealUserOnly>
								{post ? (
									post.postType === 'event' ? (
										<EventLikeDislike likeable={post} />
									) : (
										<PostLikeDislike likeable={post} />
									)
								) : null}
							</RealUserOnly>
						)}
					</>
				}
			/>
			<QuirkIonContent flush>
				<QuirkLoader
					isLoading={isLoading}
					loadingMessage="Loading post..."
					data={post}
					noResults={
						<NoResults
							heading="No post found."
							subheading="There is no post here. Choose another."
							icon={<IconSadFace />}
						/>
					}
				>
					<>
						<div className={styles.postContainer}>
							{post && <PostListItem post={post} detail={true} expanded={expanded === 'expanded'} />}
							<QuirkLoader
								isLoading={commentsLoading}
								compact
								loadingMessage={`Loading ${getLoadingMessage(post?.postType)}...`}
								data={commentData}
								noResults={
									<>
										<RealUserOnly>
											{post?.displayComments && (
												<NoResults
													compact
													heading={`No ${
														post?.postType === 'communityQuestion' ? 'answers' : 'comments'
													} yet.`}
													icon={<IconComments />}
												>
													<IonRouterLink
														routerLink={`/main/latest/post/reply/${post?._id}/detail`}
													>
														Create one.
													</IonRouterLink>
												</NoResults>
											)}
											{!post?.displayComments && (
												<NoResults
													icon={<IconEyeOff />}
													testId="CommentsHiddenMessage"
													heading={'Comments hidden'}
												/>
											)}
										</RealUserOnly>
									</>
								}
							>
								<RealUserOnly>
									{commentData && (
										<>
											{post?.displayComments && (
												<div
													className={styles.commentsContainer}
													data-testid="PostCommentsContainer"
												>
													{post.postType === 'marketplaceItem' && (
														<PrivateConversationBanner
															plural={post.author === user._id}
															seller={post.author === user._id}
														/>
													)}
													{commentDataWithTotalStars &&
														post &&
														commentDataWithTotalStars.map((item: FullComment) => (
															<NestedCommentItem
																commentState={commentState}
																key={item._id}
																post={post}
																starredComments={starredComments}
																comment={item}
																levels={post.postType === 'marketplaceItem' ? 2 : 3}
																commentId={commentid}
																expanded={
																	commentid !== undefined
																		? getCommentDepth(
																				item.fullChildren,
																				commentid
																		  ) >= 0
																		: false
																}
																testId={item._id}
															/>
														))}
												</div>
											)}
										</>
									)}
								</RealUserOnly>
							</QuirkLoader>
							<AnonymousOnly>
								<FeedLogin
									label={`Login to view or create ${
										post?.postType === 'communityQuestion' ? 'answers' : 'comments'
									} `}
								/>
							</AnonymousOnly>
						</div>
					</>
				</QuirkLoader>
			</QuirkIonContent>
		</QuirkIonPage>
	);
};
